const LEGACY_HOUR_LABELS = [
    { key: 'videos', label: 'Videos grabados' },
    { key: 'liveClasses', label: 'Clases en vivo' },
    { key: 'readings', label: 'Lectura' },
    { key: 'writing', label: 'Escritura' },
];
/**
 * Coerce any stored/extracted `expectedHours` into the dynamic list shape.
 * - array → ensures each item has an id/label/hours;
 * - legacy object {videos,...} → one row per field with hours > 0 (es labels);
 * - null/undefined/empty → [].
 */
export function normalizeExpectedHours(raw) {
    if (Array.isArray(raw)) {
        return raw
            .map((it, i) => {
            const item = (it ?? {});
            return {
                id: typeof item.id === 'string' && item.id ? item.id : `eh-${i}`,
                label: typeof item.label === 'string' ? item.label : '',
                hours: Number(item.hours) || 0,
            };
        })
            .filter((it) => it.label.trim() !== '' || it.hours > 0);
    }
    if (raw && typeof raw === 'object') {
        const legacy = raw;
        return LEGACY_HOUR_LABELS.filter(({ key }) => (Number(legacy[key]) || 0) > 0).map(({ key, label }) => ({ id: `legacy-${key}`, label, hours: Number(legacy[key]) || 0 }));
    }
    return [];
}
export const SCHEDULE_STYLES = ['weekly', 'sessions'];
/**
 * Narrow an unknown stored value to a `ScheduleStyle`.
 *
 * The write path does not validate: `updateCourseTemplate` casts its patch and the use
 * case takes it typed off the props, so a typo ('session') persists happily. Without
 * this guard it would flow out of `resolveScheduleStyle` and reach a renderer that
 * switches on the two known values — which would emit no calendar at all, for a
 * template that looks fine in the editor. Anything unrecognized is treated as absent,
 * so the named fallback decides instead.
 */
export function asScheduleStyle(raw) {
    return SCHEDULE_STYLES.includes(raw) ? raw : undefined;
}
/**
 * Merge the reading plan and the assignments into one week-indexed calendar — the
 * single table the printed syllabus shows ("Semana 1: leer X … entregar Y").
 *
 * They used to be two unrelated tables in the document because readings carried no
 * week at all; `ReadingPlanRow.weekOffset` is what makes this join possible.
 */
export function buildCourseCalendar(planRows, assignments) {
    const byWeek = new Map();
    const orphanReadings = [];
    const orphanAssignments = [];
    // `weekOffset` is typed as a required number on an assignment, but stored data can
    // still carry null: clearing the "Sem #" field in the editor produces NaN, which
    // becomes null through JSON. Guarding both sides keeps such a row out of week 0,
    // where an unguarded lookup would quietly file it as due in the first week.
    const placedIn = (w) => {
        if (typeof w !== 'number' || !Number.isFinite(w))
            return null;
        let bucket = byWeek.get(w);
        if (!bucket) {
            bucket = { weekOffset: w, readings: [], assignments: [] };
            byWeek.set(w, bucket);
        }
        return bucket;
    };
    for (const row of planRows ?? []) {
        const bucket = placedIn(row.weekOffset);
        if (bucket)
            bucket.readings.push(row);
        else
            orphanReadings.push(row);
    }
    for (const a of assignments) {
        const entry = { id: a.id, name: a.name, type: a.type };
        const bucket = placedIn(a.weekOffset);
        if (bucket)
            bucket.assignments.push(entry);
        else
            orphanAssignments.push(entry);
    }
    // Every bucket in the map has a numeric week by construction, so the comparator
    // needs no fallback — the nullable bucket is appended after sorting, never sorted.
    const weeks = [...byWeek.values()].sort((a, b) => a.weekOffset - b.weekOffset);
    if (orphanReadings.length > 0 || orphanAssignments.length > 0) {
        weeks.push({ weekOffset: null, readings: orphanReadings, assignments: orphanAssignments });
    }
    return weeks;
}
/**
 * La tabla de horas que imprime el syllabus: las evaluaciones que declaran horas
 * PRIMERO, en el orden de la tabla de porcentajes, y después la dedicación que
 * no se califica.
 *
 * UNA SOLA TABLA aunque sean dos listas. El documento imprime un único bloque
 * con un TOTAL, y ese total es el número que el alumno busca; partirlo en dos
 * tablas obligaría a sumarlas de cabeza. De dónde viene cada fila queda en
 * `graded` para quien lo necesite —el CCD las clasifica igual por su rótulo—,
 * pero no cambia lo impreso.
 *
 * Las filas en cero se omiten: una evaluación sin horas declaradas no afirma que
 * no lleve tiempo, sólo que nadie lo estimó.
 */
export function buildWorkloadRows(components, 
/** `unknown` a propósito: `normalizeExpectedHours` acepta también la forma
 *  vieja (objeto fijo {videos, liveClasses, …}) que todavía hay almacenada. */
expectedHours) {
    const rows = [];
    for (const c of components ?? []) {
        const hours = Number(c.hours) || 0;
        if (hours > 0)
            rows.push({ id: c.id, label: c.name, hours, graded: true });
    }
    for (const it of normalizeExpectedHours(expectedHours)) {
        const hours = Number(it.hours) || 0;
        if (hours > 0)
            rows.push({ id: it.id, label: it.label, hours, graded: false });
    }
    return { rows, total: rows.reduce((sum, r) => sum + r.hours, 0) };
}
/**
 * Build the evaluation × course-objective matrix the syllabus prints.
 *
 * Orphan ids are IGNORED rather than treated as an error. Deleting a course objective
 * is a normal editorial act, and it must not be able to break the rendering of a
 * document — the alternative (validating on write) would make the objective
 * undeletable while any evaluation still referenced it. The cost is that a
 * mistyped id silently marks nothing, which is visible in the rendered matrix.
 */
export function buildAssignmentDistribution(components, courseObjectives) {
    if (!components || components.length === 0)
        return [];
    return components.map((c) => {
        const marked = new Set(c.objectiveIds ?? []);
        return {
            componentId: c.id,
            title: c.name,
            marks: courseObjectives.map((o) => marked.has(o.id)),
        };
    });
}
/**
 * The calendar shape to render for a template.
 *
 * The fallback is spelled out rather than left to a `??` because it DECIDES THE SHAPE
 * OF A DOCUMENT the student reads, and a template authored before `scheduleStyle`
 * existed has no answer of its own. A course that declared class days wants the
 * session calendar; one that never did cannot fill a date/time table at all, so the
 * weekly form is the only one it can render. Naming it here keeps the choice visible
 * instead of hiding it in whichever renderer happens to ask first.
 */
export function resolveScheduleStyle(tpl) {
    if (tpl.scheduleStyle)
        return tpl.scheduleStyle;
    const declaresClassDays = tpl.modules.some((m) => (m.dayOffsets?.length ?? 0) > 0);
    return declaresClassDays ? 'sessions' : 'weekly';
}
const EMPTY_TIPTAP = {
    type: 'doc',
    content: [{ type: 'paragraph' }],
};
export class CourseTemplate {
    props;
    constructor(props) {
        this.props = props;
    }
    static create(params) {
        if (!params.title.trim())
            throw new Error('Template title is required');
        const now = new Date();
        return new CourseTemplate({
            id: params.id,
            tenantId: params.tenantId,
            ...(params.code ? { code: params.code.trim() } : {}),
            ...(params.courseId ? { courseId: params.courseId } : {}),
            ...(params.programId ? { programId: params.programId } : {}),
            title: params.title.trim(),
            version: 1,
            status: 'draft',
            description: EMPTY_TIPTAP,
            objectives: { program: [], department: [], course: [] },
            philosophy: [],
            grading: {
                components: [],
                passingGrade: 4,
                expectedHours: [],
            },
            readings: { required: [], complementary: { items: [] } },
            sessionFormat: { weekdayTemplates: [] },
            modules: [],
            assignments: [],
            ...(params.source ? { source: params.source } : {}),
            createdAt: now,
            createdBy: params.createdBy,
            updatedAt: now,
        });
    }
    static reconstitute(props) {
        // Normalize legacy fixed-shape expectedHours into the dynamic list.
        return new CourseTemplate({
            ...props,
            grading: {
                ...props.grading,
                expectedHours: normalizeExpectedHours(props.grading?.expectedHours),
            },
        });
    }
    get id() {
        return this.props.id;
    }
    get tenantId() {
        return this.props.tenantId;
    }
    get courseId() {
        return this.props.courseId;
    }
    get programId() {
        return this.props.programId;
    }
    get title() {
        return this.props.title;
    }
    get code() {
        return this.props.code;
    }
    get version() {
        return this.props.version;
    }
    get status() {
        return this.props.status;
    }
    get description() {
        return this.props.description;
    }
    get objectives() {
        return this.props.objectives;
    }
    get philosophy() {
        return [...this.props.philosophy];
    }
    get grading() {
        return this.props.grading;
    }
    get readings() {
        return this.props.readings;
    }
    get sessionFormat() {
        return this.props.sessionFormat;
    }
    get modules() {
        return [...this.props.modules];
    }
    get assignments() {
        return [...this.props.assignments];
    }
    get latePolicy() {
        return this.props.latePolicy;
    }
    get anchorVerse() {
        return this.props.anchorVerse;
    }
    get biblicalVersion() {
        return this.props.biblicalVersion;
    }
    get institutionalFramework() {
        return this.props.institutionalFramework;
    }
    get source() {
        return this.props.source;
    }
    get attendancePolicy() {
        return this.props.attendancePolicy;
    }
    get conductRules() {
        return this.props.conductRules;
    }
    get createdAt() {
        return this.props.createdAt;
    }
    get createdBy() {
        return this.props.createdBy;
    }
    get updatedAt() {
        return this.props.updatedAt;
    }
    get deletedAt() {
        return this.props.deletedAt;
    }
    isDeleted() {
        return !!this.props.deletedAt;
    }
    /** Validates that grading components sum to 100 (±0.01). */
    validateGradingSum() {
        const sum = this.props.grading.components.reduce((s, c) => s + c.weight, 0);
        return { ok: Math.abs(sum - 100) < 0.01, sum };
    }
    /** Wholesale update — caller is responsible for assembling a valid props patch. */
    update(patch) {
        return new CourseTemplate({
            ...this.props,
            ...patch,
            version: this.props.version + 1,
            updatedAt: new Date(),
        });
    }
    softDelete(deletedBy) {
        if (this.isDeleted())
            return this;
        return new CourseTemplate({
            ...this.props,
            deletedAt: new Date(),
            deletedBy,
            updatedAt: new Date(),
        });
    }
    toSnapshot() {
        return { ...this.props };
    }
}
//# sourceMappingURL=course-template.entity.js.map